// [S-270 + S-271 + S-272] Analytics dashboard. Two scopes:
//   - tenant_admin → /analytics?scope=tenant (default; uses caller's
//     own studio).
//   - platform_admin → can toggle Platform / Tenant via the scope
//     selector at the top.
// CSV export downloads via blob.
import { useState } from "react";
import { Alert, Button, PageHead, Select } from "../components";
import { useSession } from "../auth/AuthProvider";
import { sessionCan } from "../auth/capabilities";
import {
  useAnalytics,
  useExportAnalytics,
  type AnalyticsBreakdown,
  type AnalyticsSnapshot,
} from "../data/analyticsQuery";

export function Analytics(): JSX.Element {
  const session = useSession();
  const canViewPlatform = sessionCan(session, "analytics:view_platform", {
    layer: "L1",
  });
  const tenantId =
    session?.studioId !== null && session?.studioId !== undefined
      ? `tenant-${session.studioId}`
      : undefined;

  // Scope toggle is only meaningful for platform_admin (tenant_admin
  // can only ever see their own tenant).
  const [scope, setScope] = useState<"tenant" | "platform">(
    canViewPlatform ? "platform" : "tenant"
  );

  const query = useAnalytics(
    scope,
    scope === "tenant" ? tenantId : undefined
  );
  const exportCsv = useExportAnalytics();

  return (
    <>
      <PageHead
        eyebrow="Platform · analytics"
        title={
          <>
            Usage and <em>volume.</em>
          </>
        }
        sub={
          query.isLoading
            ? "Loading…"
            : query.data === undefined
              ? ""
              : `Generated ${formatAgo(query.data.generatedAt)} · ${scope === "platform" ? "platform aggregate" : `tenant ${query.data.tenantId ?? "—"}`}`
        }
        cta={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {canViewPlatform && (
              <Select
                label="Scope"
                value={scope}
                options={[
                  { value: "tenant", label: "Organization" },
                  { value: "platform", label: "Platform" },
                ]}
                onValueChange={(v) => setScope(v ?? "tenant")}
              />
            )}
            <Button
              variant="secondary"
              className="btn--s"
              onClick={() =>
                exportCsv.mutate({
                  scope,
                  ...(scope === "tenant" && tenantId !== undefined
                    ? { tenantId }
                    : {}),
                })
              }
              disabled={exportCsv.isPending || query.data === undefined}
              data-testid="analytics-export"
            >
              {exportCsv.isPending ? "Exporting…" : "Export CSV"}
            </Button>
          </div>
        }
      />

      {query.isError && (
        <Alert label="Couldn't load" tone="error">
          {query.error?.message ?? "unknown error"}
        </Alert>
      )}

      {query.isLoading && (
        <div className="analytics__state" role="status">
          Loading analytics…
        </div>
      )}

      {!query.isLoading && !query.isError && query.data !== undefined && (
        <SnapshotView data={query.data} />
      )}
    </>
  );
}

function SnapshotView({ data }: { data: AnalyticsSnapshot }): JSX.Element {
  return (
    <>
      <div className="analytics__kpis">
        <Kpi label="Templates" value={data.templates.total} />
        <Kpi label="Reviews" value={data.reviews.total} />
        <Kpi
          label="Avg confidence"
          value={data.reviews.avgConfidence ?? "—"}
        />
        <Kpi
          label="Avg deviations / review"
          value={data.reviews.avgDeviations ?? "—"}
        />
      </div>

      <div className="analytics__grid">
        <BreakdownCard
          title="Templates · by deal type"
          rows={data.templates.byDealType}
        />
        <BreakdownCard
          title="Templates · by guild"
          rows={data.templates.byGuild}
        />
        <BreakdownCard
          title="Templates · by tier"
          rows={data.templates.byTier}
        />
        <BreakdownCard
          title="Templates · by lifecycle"
          rows={data.templates.byLifecycleState}
        />
        <BreakdownCard
          title="Reviews · by deal type"
          rows={data.reviews.byDealType}
        />
        <BreakdownCard
          title="Reviews · by guild"
          rows={data.reviews.byGuild}
        />
      </div>

      {data.scope === "platform" && (
        <div className="analytics__state">
          <b>Platform aggregate</b> · Review counts are zero for the
          platform scope until ReviewRunRepositoryPort exposes a
          cross-tenant list method. Templates are aggregated correctly.
        </div>
      )}
    </>
  );
}

function Kpi({
  label,
  value,
}: {
  label: string;
  value: number | string;
}): JSX.Element {
  return (
    <div className="analytics__kpi">
      <div className="analytics__kpi__lbl">{label}</div>
      <div className="analytics__kpi__num">
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

// [S-683] Not paginated, deliberately: rows are grouped by a bounded enum
// (deal type, guild, talent tier, lifecycle state — each under a dozen
// values), not a growing record list, so there's no unbounded-render risk
// pagination would guard against.
function BreakdownCard({
  title,
  rows,
}: {
  title: string;
  rows: AnalyticsBreakdown[];
}): JSX.Element {
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0);
  return (
    <div className="analytics__card">
      <div className="analytics__card__title">{title}</div>
      {rows.length === 0 ? (
        <div className="analytics__card__empty">No data yet.</div>
      ) : (
        <ul className="analytics__bars">
          {rows.map((r) => (
            <li key={r.key} className="analytics__bar">
              <span className="analytics__bar__label">{prettyKey(r.key)}</span>
              <span
                className="analytics__bar__fill"
                style={{ width: `${max > 0 ? (r.count / max) * 100 : 0}%` }}
                aria-hidden="true"
              />
              <span className="analytics__bar__count">{r.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function prettyKey(k: string): string {
  // Drop underscores, title-case-ish.
  return k.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function formatAgo(iso: string): string {
  const diff = Date.now() - Date.parse(iso);
  const m = Math.round(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  return `${Math.round(m / 60)}h ago`;
}
